import { briefToMarkdown } from "../domain/brief";
import type { GeneratedBrief } from "../domain/types";

type Props = {
  brief: GeneratedBrief;
  onReturn: () => void;
  onSend: () => void;
};

function download(name: string, content: string, type: string) {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function BriefView({ brief, onReturn, onSend }: Props) {
  const safeName = brief.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "deck-brief";
  return (
    <main className="brief-view" id="main">
      <header className="brief-hero">
        <p className="mono-label">YOUR WORKING DECK BRIEF</p>
        <h1>{brief.title}</h1>
        <p>{brief.routeLabel}</p>
        <div className="brief-actions no-print">
          <button className="quiet-button" type="button" onClick={onReturn}>Keep answering</button>
          <button className="quiet-button" type="button" onClick={() => download(`${safeName}.md`, briefToMarkdown(brief), "text/markdown")}>Download brief</button>
          <button className="quiet-button" type="button" onClick={() => window.print()}>Save as PDF</button>
          <button className="ink-button" type="button" onClick={onSend}>Send to pitch.dog</button>
        </div>
      </header>

      <section className="brief-chapter brief-project">
        <p className="mono-label">01 · PROJECT + DECISION</p>
        <div className="brief-facts">
          {brief.projectBrief.map((item) => (
            <div key={item.label}><dt>{item.label}</dt><dd>{item.value}</dd></div>
          ))}
        </div>
      </section>

      <section className="brief-chapter">
        <p className="mono-label">02 · RECOMMENDED DECK STORY</p>
        <ol className="story-outline">
          {brief.storyOutline.map((item, index) => (
            <li key={`${item.title}-${index}`}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <div><h2>{item.title}</h2><p>{item.purpose}</p></div>
            </li>
          ))}
        </ol>
      </section>

      <section className="brief-chapter readiness-chapter">
        <p className="mono-label">03 · DECK READINESS MAP</p>
        <h2>Not a score. A map of where the thinking stands.</h2>
        <div className="readiness-list">
          {brief.readiness.map((item) => (
            <article key={item.id} data-state={item.state}>
              <p className="mono-label">{item.state.replace("_", " ")}</p>
              <h3>{item.label}</h3>
              <p>{item.reason}</p>
            </article>
          ))}
        </div>
      </section>

      <div className="brief-two-up">
        <section className="brief-chapter">
          <p className="mono-label">04 · ASSETS</p>
          <h2>What the work needs.</h2>
          <ul className="check-list">{brief.assetChecklist.map((item) => <li key={item}>{item}</li>)}</ul>
        </section>
        <section className="brief-chapter">
          <p className="mono-label">05 · EVIDENCE</p>
          <h2>What should be inspectable.</h2>
          {brief.evidenceIndex.map((item, index) => (
            <div className="evidence-row" key={index}>
              <span>{item.state.replace("_", " ")}</span>
              <p>{item.claim}</p>
              <small>{item.source}</small>
            </div>
          ))}
        </section>
      </div>

      <section className="brief-chapter risk-room">
        <div>
          <p className="mono-label">06 · OPEN QUESTIONS + RISKS</p>
          <h2>Keep the gaps visible.</h2>
          <ul>{brief.openQuestions.map((item) => <li key={item}>{item}</li>)}</ul>
          <ul>{brief.risks.map((item) => <li key={item}>{item}</li>)}</ul>
        </div>
        <div>
          <p className="mono-label">DO NOT INVENT OR IMPLY</p>
          <ul>{brief.doNotInvent.map((item) => <li key={item}>{item}</li>)}</ul>
        </div>
      </section>

      <section className="brief-chapter next-room">
        <p className="mono-label">07 · DO NEXT</p>
        <ol>{brief.nextActions.map((item) => <li key={item}>{item}</li>)}</ol>
      </section>
    </main>
  );
}
